/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {isDeliveryAddressFilled, isEGiftCardInBasket} from '../../../utils/basket-utils'
import {paidWithGC} from '../../../api/utils/gcHelpers'
import {PAYMENT_METHODS} from '../../../api/utils/constants'

const CheckoutContext = React.createContext()

export const CheckoutProvider = ({children}) => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket, derivedData, isLoading: isBasketLoading} = useCurrentBasket()
    const einstein = useEinstein()
    const [step, setStep] = useState()
    const returnToReviewRef = useRef(false)

    const CHECKOUT_STEPS_LIST = [
        'CONTACT_INFO',
        'SHIPPING_ADDRESS',
        'SHIPPING_OPTIONS',
        'PAYMENT',
        'REVIEW_ORDER'
    ]
    const STEPS = CHECKOUT_STEPS_LIST.reduce((acc, step, idx) => ({...acc, [step]: idx}), {})

    const getCheckoutStepName = (step) => CHECKOUT_STEPS_LIST[step]

    useEffect(() => {
        if (
            isBasketLoading ||
            !customer ||
            !basket ||
            sessionStorage.getItem('isBoltLoading') === 'true'
        ) {
            return
        }

        if (returnToReviewRef.current) {
            return
        }

        let step = STEPS.REVIEW_ORDER
        const isEGiftCardOnly = isEGiftCardInBasket(basket)
        const validShippingAddress = isDeliveryAddressFilled(basket)
        const isBopisShipment = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
        const contactDetailsMissing =
            (isBopisShipment || isEGiftCardOnly) &&
            !(basket?.customerInfo?.email || customer?.email)
        const forceOpenContactDetails =
            localStorage.getItem('customerSelectedContactInfo') !== 'true' &&
            (isBopisShipment || isEGiftCardOnly)
        const forceOpenShippingAddress =
            localStorage.getItem('customerSelectedShippingAddress') === 'false'
        const forceOpenShippingMethods =
            localStorage.getItem('customerSelectedShippingMethod') !== 'true'
        if (contactDetailsMissing || forceOpenContactDetails) {
            step = STEPS.CONTACT_INFO
        } else if (
            customer.isGuest &&
            !basket.customerInfo?.email &&
            !isEGiftCardOnly &&
            !isBopisShipment
        ) {
            step = STEPS.SHIPPING_ADDRESS
        } else if (
            (!validShippingAddress || forceOpenShippingAddress) &&
            !isEGiftCardOnly &&
            !isBopisShipment
        ) {
            step = STEPS.SHIPPING_ADDRESS
        } else if (
            (derivedData?.isMissingShippingMethod && !isEGiftCardOnly && !isBopisShipment) ||
            forceOpenShippingMethods
        ) {
            step = STEPS.SHIPPING_OPTIONS
        } else if (
            !basket.paymentInstruments ||
            !basket.billingAddress ||
            localStorage.getItem('forceOpenPaymentToggle') === 'true'
        ) {
            step = STEPS.PAYMENT
        }

        if (step === STEPS.REVIEW_ORDER) {
            const paidWithGiftCard = paidWithGC(basket)
            const hasBoltPayment = basket?.paymentInstruments?.find(
                (payment) => payment.paymentMethodId === PAYMENT_METHODS.BOLT_PAY
            )

            // if the order is fully or parially paid with GC and not Bolt, redirect to payment step
            if (
                paidWithGiftCard <= basket?.orderTotal &&
                !(sessionStorage.getItem('isBoltShopperLoggedIn') && hasBoltPayment)
            ) {
                step = STEPS.PAYMENT
            }
        }

        setStep(step)
    }, [
        isBasketLoading,
        customer?.isGuest,
        basket?.customerInfo?.email,
        basket?.shipments,
        basket?.paymentInstruments,
        basket?.billingAddress,
        derivedData?.isMissingShippingAddress,
        derivedData?.isMissingShippingMethod
    ])

    /**************** Einstein ****************/
    // Run this once when checkout begins
    useEffect(() => {
        if (basket?.productItems) {
            einstein.sendBeginCheckout(basket)
        }
    }, [])

    // Run this every time checkout steps change
    useEffect(() => {
        if (step != undefined) {
            einstein.sendCheckoutStep(getCheckoutStepName(step), step, basket)
        }
    }, [step])

    const goToNextStep = () => {
        if (returnToReviewRef.current) {
            returnToReviewRef.current = false

            if (sessionStorage.getItem('isBoltShopperLoggedIn')) {
                setStep(STEPS.REVIEW_ORDER)
            } else {
                setStep(STEPS.PAYMENT)
            }
            return
        }

        if (step === STEPS.SHIPPING_ADDRESS) {
            const shouldSkipDirectlyToPayment =
                derivedData?.totalDeliveryShipments === 0 && derivedData?.totalPickupShipments === 1
            if (shouldSkipDirectlyToPayment) {
                setStep(STEPS.PAYMENT)
                return
            }

            setStep(STEPS.SHIPPING_ADDRESS)
        } else {
            setStep(step + 1)
        }
    }

    const goToStep = (targetStep) => {
        if (step === STEPS.REVIEW_ORDER && targetStep !== STEPS.REVIEW_ORDER) {
            returnToReviewRef.current = true
            setStep(targetStep)
            return
        }

        if (returnToReviewRef.current && step === STEPS.REVIEW_ORDER) {
            returnToReviewRef.current = false

            if (sessionStorage.getItem('isBoltShopperLoggedIn')) {
                setStep(STEPS.REVIEW_ORDER)
            } else {
                setStep(STEPS.PAYMENT)
            }
            return
        }

        setStep(targetStep)
    }

    const value = {
        step,
        STEPS,
        goToNextStep,
        goToStep
    }

    return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>
}

CheckoutProvider.propTypes = {
    children: PropTypes.any
}

/**
 * A hook for managing checkout state and actions
 * @returns {Object} Checkout data and actions
 */
export const useCheckout = () => {
    return React.useContext(CheckoutContext)
}
